import { RiArrowLeftSLine, RiArrowRightSLine } from '@remixicon/react';
import { cn } from '@/shared/lib/cn';
import { IconButton } from './icon-button';

export interface PaginationProps {
  /** Current page, 1-based. */
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  /**
   * `compact` — "‹ 1 / 45 ›" (legacy admin 14:17596: 32 icon buttons White/5, 400 14/20 White/90, gap 8).
   * `pages` — numbered 32 buttons with ellipsis; the current page is accent.
   */
  variant?: 'compact' | 'pages';
  /** Pages shown on each side of the current one (`pages`). Default 1. */
  siblingCount?: number;
  labels?: { previous?: string; next?: string; page?: (n: number) => string };
  disabled?: boolean;
  className?: string;
}

type PageToken = number | 'gap-start' | 'gap-end';

function pageTokens(page: number, count: number, siblings: number): PageToken[] {
  const total = siblings * 2 + 5;
  if (count <= total) return Array.from({ length: count }, (_, i) => i + 1);
  const left = Math.max(page - siblings, 1);
  const right = Math.min(page + siblings, count);
  const showLeftGap = left > 3;
  const showRightGap = right < count - 2;
  if (!showLeftGap) {
    const width = siblings * 2 + 3;
    return [...Array.from({ length: width }, (_, i) => i + 1), 'gap-end', count];
  }
  if (!showRightGap) {
    const width = siblings * 2 + 3;
    return [1, 'gap-start', ...Array.from({ length: width }, (_, i) => count - width + 1 + i)];
  }
  return [1, 'gap-start', ...Array.from({ length: right - left + 1 }, (_, i) => left + i), 'gap-end', count];
}

export function Pagination({
  page,
  pageCount,
  onPageChange,
  variant = 'compact',
  siblingCount = 1,
  labels,
  disabled,
  className,
}: PaginationProps) {
  const l = { previous: 'Oldingi sahifa', next: 'Keyingi sahifa', page: (n: number) => `${n}-sahifa`, ...labels };
  const count = Math.max(1, pageCount);
  const current = Math.min(Math.max(1, page), count);
  const go = (p: number) => {
    if (p >= 1 && p <= count && p !== current) onPageChange(p);
  };
  const prev = (
    <IconButton icon={RiArrowLeftSLine} variant="secondary" aria-label={l.previous} disabled={disabled || current <= 1} onClick={() => go(current - 1)} />
  );
  const next = (
    <IconButton icon={RiArrowRightSLine} variant="secondary" aria-label={l.next} disabled={disabled || current >= count} onClick={() => go(current + 1)} />
  );

  if (variant === 'compact') {
    return (
      <nav aria-label="Sahifalash" className={cn('flex items-center gap-8', className)}>
        {prev}
        <span className="min-w-37 text-center text-sm text-fg-nav tabular-nums" aria-live="polite">
          {current} / {count}
        </span>
        {next}
      </nav>
    );
  }

  return (
    <nav aria-label="Sahifalash" className={cn('flex items-center gap-4', className)}>
      {prev}
      {pageTokens(current, count, siblingCount).map((token) =>
        typeof token === 'number' ? (
          <button
            key={token}
            type="button"
            disabled={disabled}
            aria-label={l.page(token)}
            aria-current={token === current ? 'page' : undefined}
            onClick={() => go(token)}
            className={cn(
              'flex h-32 min-w-32 cursor-pointer items-center justify-center rounded-8 px-8 text-sm font-medium tabular-nums transition-colors duration-150',
              'disabled:cursor-not-allowed disabled:opacity-40',
              token === current ? 'bg-accent text-on-accent' : 'text-fg-nav hover:bg-hover',
            )}
          >
            {token}
          </button>
        ) : (
          <span key={token} aria-hidden className="flex h-32 min-w-24 items-center justify-center text-sm text-fg-tertiary">
            …
          </span>
        ),
      )}
      {next}
    </nav>
  );
}
